import React, { useEffect, useRef, useState } from 'react';
import {
  Loader2, Plus, Save, Trash2, ArrowUp, ArrowDown, Image as ImageIcon,
  Check, AlertCircle, Pencil, X, UploadCloud, EyeOff, Eye,
} from 'lucide-react';
import { adminApi } from '@/lib/adminApi';

/*
 * Empty testimonial template. Matches the backend `TestimonialIn` schema so a
 * freshly-created card can be POSTed without extra munging.
 */
const EMPTY = {
  name_en: '', name_ka: '',
  city_en: '', city_ka: '',
  pet_en: '', pet_ka: '',
  quote_en: '', quote_ka: '',
  avatar_url: '',
  active: true,
};

export default function AdminTestimonials() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editingId, setEditingId] = useState(null); // 'new' | existing id
  const [draft, setDraft] = useState(EMPTY);
  const [savingId, setSavingId] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [savedAt, setSavedAt] = useState(0);
  const fileInputRef = useRef(null);

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await adminApi.listTestimonials();
      setItems(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(e.message || 'Failed to load testimonials');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const beginAdd = () => {
    setEditingId('new');
    setDraft({ ...EMPTY });
  };

  const beginEdit = (item) => {
    setEditingId(item.id);
    setDraft({ ...EMPTY, ...item });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setDraft(EMPTY);
  };

  const upd = (field) => (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setDraft((d) => ({ ...d, [field]: value }));
  };

  const saveDraft = async () => {
    setSavingId(editingId);
    setError('');
    try {
      if (editingId === 'new') {
        await adminApi.createTestimonial(draft);
      } else {
        await adminApi.updateTestimonial(editingId, draft);
      }
      await load();
      cancelEdit();
      setSavedAt(Date.now());
    } catch (e) {
      setError(e.message || 'Save failed');
    } finally {
      setSavingId(null);
    }
  };

  const removeItem = async (item) => {
    if (!window.confirm(`Delete testimonial from "${item.name_en || item.name_ka || 'this member'}"?`)) return;
    setSavingId(item.id);
    setError('');
    try {
      await adminApi.deleteTestimonial(item.id);
      await load();
      if (editingId === item.id) cancelEdit();
    } catch (e) {
      setError(e.message || 'Delete failed');
    } finally {
      setSavingId(null);
    }
  };

  const move = async (item, dir) => {
    const idx = items.findIndex((x) => x.id === item.id);
    if (idx < 0) return;
    const target = idx + dir;
    if (target < 0 || target >= items.length) return;
    const newOrder = [...items];
    newOrder.splice(target, 0, newOrder.splice(idx, 1)[0]);
    setItems(newOrder); // optimistic
    try {
      await adminApi.reorderTestimonials(newOrder.map((x) => x.id));
    } catch (e) {
      setError(e.message || 'Reorder failed');
      load();
    }
  };

  const toggleActive = async (item) => {
    setSavingId(item.id);
    try {
      await adminApi.updateTestimonial(item.id, { ...item, active: !item.active });
      await load();
    } catch (e) {
      setError(e.message || 'Update failed');
    } finally {
      setSavingId(null);
    }
  };

  const onPickFile = () => fileInputRef.current?.click();

  const onUpload = async (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = '';
    if (!file) return;
    setUploading(true);
    setError('');
    try {
      const res = await adminApi.uploadImage(file);
      setDraft((d) => ({ ...d, avatar_url: res.url }));
    } catch (err) {
      setError(err.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-testimonials">
      <header className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <div>
          <p className="text-[11px] uppercase tracking-[0.3em] text-[#F25C05] font-bold">Content</p>
          <h1 className="font-display font-extrabold text-2xl md:text-3xl text-[#05223D] mt-1">
            Testimonials
          </h1>
          <p className="mt-2 text-sm text-[#465B70] max-w-2xl">
            Quotes shown in the strip above the Contact block. The public page shows the first four active cards.
          </p>
        </div>

        <button
          type="button"
          onClick={beginAdd}
          disabled={editingId !== null}
          data-testid="admin-testimonials-add"
          className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-[#F25C05] text-white text-sm font-bold shadow-[0_6px_20px_rgba(242,92,5,0.25)] hover:bg-[#D44D04] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
        >
          <Plus size={14} /> Add testimonial
        </button>
      </header>

      {error && (
        <div className="flex items-start gap-2 p-3 rounded-xl bg-red-50 border border-red-200 text-sm text-red-900" role="alert">
          <AlertCircle size={16} className="mt-0.5 shrink-0" />
          <span>{error}</span>
        </div>
      )}
      {savedAt > 0 && editingId === null && (
        <div className="flex items-center gap-2 p-3 rounded-xl bg-emerald-50 border border-emerald-200 text-sm text-emerald-900" role="status">
          <Check size={16} /> Saved. The public site is already showing the latest list.
        </div>
      )}

      {editingId !== null && (
        <TestimonialForm
          draft={draft}
          onChange={upd}
          onCancel={cancelEdit}
          onSave={saveDraft}
          saving={savingId === editingId}
          uploading={uploading}
          onPickFile={onPickFile}
          isNew={editingId === 'new'}
        />
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={onUpload}
      />

      {loading ? (
        <div className="flex items-center justify-center py-20 text-[#465B70]">
          <Loader2 className="w-6 h-6 animate-spin" />
        </div>
      ) : items.length === 0 ? (
        <div className="bg-white border border-dashed border-[#eadfce] rounded-2xl py-14 px-6 text-center">
          <p className="font-display font-bold text-[#05223D]">No testimonials yet.</p>
          <p className="mt-1 text-sm text-[#465B70]">
            Add your first member quote to populate the strip on the home page.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((item, idx) => (
            <TestimonialRow
              key={item.id}
              item={item}
              index={idx}
              total={items.length}
              onEdit={() => beginEdit(item)}
              onDelete={() => removeItem(item)}
              onMoveUp={() => move(item, -1)}
              onMoveDown={() => move(item, 1)}
              onToggleActive={() => toggleActive(item)}
              busy={savingId === item.id}
              disabled={editingId !== null}
            />
          ))}
        </div>
      )}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Row                                                                        */
/* -------------------------------------------------------------------------- */

function TestimonialRow({ item, index, total, onEdit, onDelete, onMoveUp, onMoveDown, onToggleActive, busy, disabled }) {
  const name = item.name_en || item.name_ka || '—';
  const quote = item.quote_en || item.quote_ka || '';
  const inactive = !item.active;
  return (
    <article
      data-testid={`admin-testimonial-row-${item.id}`}
      className={`flex items-start gap-4 bg-white border rounded-2xl p-4 md:p-5 transition-opacity ${inactive ? 'border-[#eadfce] opacity-60' : 'border-[#eadfce]'}`}
    >
      <div className="shrink-0 flex flex-col items-center gap-1">
        {item.avatar_url ? (
          <img
            src={item.avatar_url}
            alt={name}
            className="w-14 h-14 rounded-full object-cover border border-[#eadfce]"
          />
        ) : (
          <div className="w-14 h-14 rounded-full bg-[#F5F2EB] text-[#9CA7B4] flex items-center justify-center">
            <ImageIcon size={18} />
          </div>
        )}
        <span className="text-[10px] font-bold text-[#9CA7B4]">#{index + 1}</span>
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <p className="font-display font-bold text-[#05223D] text-sm md:text-base truncate">
            {name}
          </p>
          {(item.city_en || item.city_ka) && (
            <span className="text-[11px] text-[#465B70]">
              {item.city_en || item.city_ka}
            </span>
          )}
          {(item.pet_en || item.pet_ka) && (
            <span className="text-[10px] font-bold uppercase tracking-wider text-[#F25C05]">
              · {item.pet_en || item.pet_ka}
            </span>
          )}
          {inactive && (
            <span className="text-[10px] font-bold uppercase tracking-wider text-[#9CA7B4] border border-[#9CA7B4]/40 rounded-full px-2 py-0.5">
              Hidden
            </span>
          )}
        </div>
        <p className="mt-1 text-[13px] text-[#334155] leading-relaxed line-clamp-2">
          “{quote}”
        </p>
      </div>

      <div className="shrink-0 flex items-center gap-1">
        <button
          type="button"
          onClick={onMoveUp}
          disabled={index === 0 || disabled || busy}
          className="w-8 h-8 rounded-full border border-[#eadfce] text-[#0A4D8C] flex items-center justify-center hover:bg-[#0A4D8C]/5 disabled:opacity-30 disabled:cursor-not-allowed"
          aria-label="Move up"
          data-testid={`admin-testimonial-up-${item.id}`}
        >
          <ArrowUp size={14} />
        </button>
        <button
          type="button"
          onClick={onMoveDown}
          disabled={index === total - 1 || disabled || busy}
          className="w-8 h-8 rounded-full border border-[#eadfce] text-[#0A4D8C] flex items-center justify-center hover:bg-[#0A4D8C]/5 disabled:opacity-30 disabled:cursor-not-allowed"
          aria-label="Move down"
          data-testid={`admin-testimonial-down-${item.id}`}
        >
          <ArrowDown size={14} />
        </button>
        <button
          type="button"
          onClick={onToggleActive}
          disabled={disabled || busy}
          className="w-8 h-8 rounded-full border border-[#eadfce] text-[#0A4D8C] flex items-center justify-center hover:bg-[#0A4D8C]/5 disabled:opacity-30"
          aria-label={item.active ? 'Hide' : 'Show'}
          title={item.active ? 'Hide from public site' : 'Show on public site'}
          data-testid={`admin-testimonial-toggle-${item.id}`}
        >
          {item.active ? <Eye size={14} /> : <EyeOff size={14} />}
        </button>
        <button
          type="button"
          onClick={onEdit}
          disabled={disabled || busy}
          className="w-8 h-8 rounded-full border border-[#eadfce] text-[#0A4D8C] flex items-center justify-center hover:bg-[#0A4D8C]/5 disabled:opacity-30"
          aria-label="Edit"
          data-testid={`admin-testimonial-edit-${item.id}`}
        >
          <Pencil size={14} />
        </button>
        <button
          type="button"
          onClick={onDelete}
          disabled={disabled || busy}
          className="w-8 h-8 rounded-full border border-red-200 text-red-600 flex items-center justify-center hover:bg-red-50 disabled:opacity-30"
          aria-label="Delete"
          data-testid={`admin-testimonial-delete-${item.id}`}
        >
          {busy ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
        </button>
      </div>
    </article>
  );
}

/* -------------------------------------------------------------------------- */
/* Edit / Create form                                                         */
/* -------------------------------------------------------------------------- */

function TestimonialForm({ draft, onChange, onCancel, onSave, saving, uploading, onPickFile, isNew }) {
  return (
    <section
      data-testid="admin-testimonial-form"
      className="bg-white border border-[#F25C05]/35 rounded-2xl p-5 md:p-6 shadow-[0_10px_30px_rgba(242,92,5,0.1)]"
    >
      <div className="flex items-start justify-between gap-3 mb-5">
        <div>
          <p className="text-[11px] uppercase tracking-[0.2em] text-[#F25C05] font-bold">
            {isNew ? 'New testimonial' : 'Edit testimonial'}
          </p>
          <p className="text-xs text-[#465B70] mt-1">
            Both English and Georgian are shown. Fill at least the English column — Georgian uses English as fallback.
          </p>
        </div>
        <button
          type="button"
          onClick={onCancel}
          className="w-9 h-9 rounded-full border border-[#eadfce] text-[#465B70] flex items-center justify-center hover:bg-[#FDFBF7]"
          aria-label="Cancel"
          data-testid="admin-testimonial-form-cancel"
        >
          <X size={16} />
        </button>
      </div>

      <div className="flex items-center gap-4 mb-5">
        {draft.avatar_url ? (
          <img
            src={draft.avatar_url}
            alt="Avatar preview"
            className="w-16 h-16 rounded-full object-cover border border-[#eadfce]"
          />
        ) : (
          <div className="w-16 h-16 rounded-full bg-[#F5F2EB] text-[#9CA7B4] flex items-center justify-center">
            <ImageIcon size={20} />
          </div>
        )}
        <div>
          <button
            type="button"
            onClick={onPickFile}
            disabled={uploading}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#0A4D8C] text-[#0A4D8C] text-sm font-bold hover:bg-[#0A4D8C] hover:text-white transition-colors disabled:opacity-50"
            data-testid="admin-testimonial-upload"
          >
            {uploading ? <Loader2 size={14} className="animate-spin" /> : <UploadCloud size={14} />}
            {uploading ? 'Uploading…' : (draft.avatar_url ? 'Change photo' : 'Upload pet photo')}
          </button>
          <p className="text-[11px] text-[#9CA7B4] mt-1">JPG / PNG / WEBP · up to 6 MB</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-5 gap-y-4">
        <FormField label="Name (EN)" value={draft.name_en} onChange={onChange('name_en')} placeholder="Nino K." tid="name_en" />
        <FormField label="სახელი (KA)" value={draft.name_ka} onChange={onChange('name_ka')} placeholder="ნინო კ." tid="name_ka" />

        <FormField label="City / district (EN)" value={draft.city_en} onChange={onChange('city_en')} placeholder="Vake, Tbilisi" tid="city_en" />
        <FormField label="ქალაქი / უბანი (KA)" value={draft.city_ka} onChange={onChange('city_ka')} placeholder="ვაკე, თბილისი" tid="city_ka" />

        <FormField label="Pet (EN)" value={draft.pet_en} onChange={onChange('pet_en')} placeholder="Luna · British Shorthair" tid="pet_en" />
        <FormField label="ცხოველი (KA)" value={draft.pet_ka} onChange={onChange('pet_ka')} placeholder="ლუნა · ბრიტანული კატა" tid="pet_ka" />

        <FormField label="Quote (EN)" value={draft.quote_en} onChange={onChange('quote_en')} textarea placeholder="The box arrives on time, every single time…" tid="quote_en" />
        <FormField label="ციტატა (KA)" value={draft.quote_ka} onChange={onChange('quote_ka')} textarea placeholder="მიწოდება ყოველთვის დროზე მოდის…" tid="quote_ka" />
      </div>

      <label className="mt-5 flex items-center gap-2 text-sm text-[#05223D]">
        <input
          type="checkbox"
          checked={!!draft.active}
          onChange={onChange('active')}
          className="w-4 h-4 accent-[#F25C05]"
          data-testid="admin-testimonial-active"
        />
        Show on the public site
      </label>

      <div className="mt-6 flex items-center justify-end gap-2">
        <button
          type="button"
          onClick={onCancel}
          disabled={saving}
          className="px-4 py-2 rounded-full border border-[#eadfce] text-[#465B70] text-sm font-semibold hover:bg-[#FDFBF7]"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={onSave}
          disabled={saving}
          data-testid="admin-testimonial-form-save"
          className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-[#F25C05] text-white text-sm font-bold shadow-[0_6px_20px_rgba(242,92,5,0.25)] hover:bg-[#D44D04] disabled:opacity-50"
        >
          {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
          {saving ? 'Saving…' : (isNew ? 'Create testimonial' : 'Save changes')}
        </button>
      </div>
    </section>
  );
}

function FormField({ label, value, onChange, placeholder, textarea, tid }) {
  const classes =
    'w-full rounded-xl border border-[#eadfce] bg-[#FDFBF7] px-3 py-2 text-sm text-[#05223D] placeholder:text-[#9CA7B4] focus:outline-none focus:ring-2 focus:ring-[#F25C05]/40 focus:border-[#F25C05] transition-colors';
  return (
    <label className="block">
      <span className="block text-[10.5px] font-bold uppercase tracking-[0.14em] text-[#0A4D8C] mb-1">
        {label}
      </span>
      {textarea ? (
        <textarea
          value={value || ''}
          onChange={onChange}
          placeholder={placeholder}
          rows={3}
          className={classes}
          data-testid={`admin-testimonial-field-${tid}`}
        />
      ) : (
        <input
          type="text"
          value={value || ''}
          onChange={onChange}
          placeholder={placeholder}
          className={classes}
          data-testid={`admin-testimonial-field-${tid}`}
        />
      )}
    </label>
  );
}
